import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "../lib/api";
import { config } from "../lib/config";
import {
  SEED_COMPANY_FY,
  loadLeavePolicy,
  loadLeaveRequests,
  saveLeavePolicy,
  saveLeaveRequests,
  type CompanyFy,
  type CompanyLeavePolicy,
  type LeaveRequest,
  type LeaveRequestStatus,
} from "../data/leaveSeed";
import { useAuth } from "../state/AuthContext";

export type LeaveDataSource = "api" | "local";

export function useLeaveData() {
  const { roleIds } = useAuth();
  const roleHeader = roleIds.join("|");
  const [requests, setRequests] = useState<LeaveRequest[]>(() => loadLeaveRequests());
  const [policy, setPolicy] = useState<CompanyLeavePolicy>(() => loadLeavePolicy());
  const [fy, setFy] = useState<CompanyFy>(() => ({ ...SEED_COMPANY_FY }));
  const [loading, setLoading] = useState(Boolean(config.apiUrl));
  const [waking, setWaking] = useState(false);
  const [source, setSource] = useState<LeaveDataSource>("local");
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!config.apiUrl) {
        if (!cancelled) {
          setRequests(loadLeaveRequests());
          setPolicy(loadLeavePolicy());
          setFy({ ...SEED_COMPANY_FY });
          setSource("local");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setWaking(false);
      try {
        const [reqRes, polRes, fyRes] = await Promise.all([
          apiFetch<{ rows: LeaveRequest[] }>("/leave/requests", { roleId: roleHeader }),
          apiFetch<{ policy: CompanyLeavePolicy }>("/leave/policy", { roleId: roleHeader }),
          apiFetch<{ fy: CompanyFy }>("/company/fy", { roleId: roleHeader }),
        ]);
        if (cancelled) return;
        setRequests(reqRes.rows);
        setPolicy(polRes.policy);
        setFy(fyRes.fy);
        setSource("api");
        // Cache only; API is SoT when reachable
        saveLeaveRequests(reqRes.rows);
        saveLeavePolicy(polRes.policy);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.waking) setWaking(true);
        setRequests(loadLeaveRequests());
        setPolicy(loadLeavePolicy());
        setFy({ ...SEED_COMPANY_FY });
        setSource("local");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [roleHeader, tick]);

  const persistRequests = useCallback(
    async (next: LeaveRequest[], changed?: LeaveRequest) => {
      setRequests(next);
      if (source !== "api" || !config.apiUrl) {
        saveLeaveRequests(next);
        return;
      }
      try {
        if (changed) {
          await apiFetch("/leave/requests", {
            method: "POST",
            roleId: roleHeader,
            body: changed,
          });
        }
      } catch {
        saveLeaveRequests(next);
        refresh();
      }
    },
    [source, roleHeader, refresh],
  );

  const patchRequestStatus = useCallback(
    async (
      id: string,
      status: LeaveRequestStatus,
      extras: { by?: string; note?: string; current_owner?: string } = {},
    ) => {
      const now = new Date().toISOString();
      const next = requests.map((r) =>
        r.id === id
          ? {
              ...r,
              status,
              current_owner: extras.current_owner ?? "—",
              history: [
                ...r.history,
                {
                  at: now,
                  status,
                  by: extras.by || "System",
                  ...(extras.note ? { note: extras.note } : {}),
                },
              ],
            }
          : r,
      );
      setRequests(next);

      if (source !== "api" || !config.apiUrl) {
        saveLeaveRequests(next);
        return;
      }
      try {
        await apiFetch(`/leave/requests/${encodeURIComponent(id)}`, {
          method: "PATCH",
          roleId: roleHeader,
          body: { status, ...extras },
        });
      } catch {
        saveLeaveRequests(next);
        refresh();
      }
    },
    [requests, source, roleHeader, refresh],
  );

  const persistPolicy = useCallback(
    async (next: CompanyLeavePolicy) => {
      setPolicy(next);
      if (source !== "api" || !config.apiUrl) {
        saveLeavePolicy(next);
        return;
      }
      try {
        await apiFetch("/leave/policy", {
          method: "PUT",
          roleId: roleHeader,
          body: next,
        });
      } catch {
        saveLeavePolicy(next);
        refresh();
      }
    },
    [source, roleHeader, refresh],
  );

  const persistFy = useCallback(
    async (next: CompanyFy) => {
      setFy(next);
      if (source !== "api" || !config.apiUrl) return;
      try {
        await apiFetch("/company/fy", {
          method: "PUT",
          roleId: roleHeader,
          body: next,
        });
      } catch {
        refresh();
      }
    },
    [source, roleHeader, refresh],
  );

  return {
    requests,
    policy,
    fy,
    loading,
    waking,
    source,
    refresh,
    persistRequests,
    patchRequestStatus,
    persistPolicy,
    persistFy,
  };
}
